import React, { FunctionComponent, useState } from 'react';
import TimePicker from 'react-times';
import { formatInTimeZone } from 'date-fns-tz';
import { format } from 'date-fns';
import classnames from 'classnames';
import withStyles from 'react-jss';
import { useFormContext } from 'react-hook-form';
import 'react-times/css/material/default.css';

import { ClassProp } from 'helpers/types';
import { Theme } from 'config';

import FormErrors from '../FormError';

type Time = {
  hour: string;
  minute: string;
  meridiem?: string;
};

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  name: string;
  label: string;
  placeholder?: Date;
  size?: string;
  tabIndex?: number;
  disabled?: boolean;
  useCST?: boolean;
};

const styles = (theme: Theme) => ({
  container: {
    width: '100%',
    marginTop: '.25rem !important',
    justifyContent: 'center !important',
  },
  small: {
    width: '31%',
    marginRight: '.55rem',
  },
  medium: {
    width: '48%',
    marginRight: '.55rem',
  },
  large: {
    width: '100%',
  },
  fieldContainer: {},
  fieldLabel: {
    color: '#646464 !important',
    fontSize: '.875rem !important',
    fontWeight: '500 !important',
    margin: '0 0 .30rem 0',
    display: 'block !important',
  },
  fieldInput: {
    display: 'flex',
    flex: '1 0 auto',
    maxWidth: '100%',
    width: '100%',
    textAlign: 'left',
    margin: '0',
    outline: '0',
    tapHighlightColor: 'rgba(255,255,255,0)',
    lineHeight: '1.21em',
    padding: '.67857143rem 1rem',
    fontSize: '1em',
    background: '#fff',
    border: '.0625rem solid rgba(34,36,38,.15)',
    color: 'rgba(0,0,0,.87)',
    borderRadius: '.28571429rem',
    boxShadow: '0 0 0 0 transparent inset',
    transition: 'color .1s ease,border-color .1s ease',
  },
  fieldError: {},
  timePickerWrapper: {
    width: '100%',
  },
  '@global .react_times_button': {
    maxWidth: '100%',
    width: '100%',
    margin: '0 !important',
    outline: '0 !important',
    tapHighlightColor: 'rgba(255,255,255,0) !important',
    lineHeight: '1.21em !important',
    padding: '.67857143rem 1rem !important',
    fontSize: '1em !important',
    background: '#fff !important',
    border: '.0625rem solid rgba(34,36,38,.15) !important',
    color: 'rgba(0,0,0,.87) !important',
    borderRadius: '.28571429rem  !important',
    boxShadow: '0 0 0 0 transparent inset !important',
    transition: 'color .1s ease,border-color .1s ease !important',
  },
  '@global .outside_container.active .modal_container': {
    width: 'auto !important',
    borderRadius: '.28571429rem  !important',
    transform: 'scale(1) translateY(.0625rem) !important',
  },
  '@global .time_picker_preview': {
    height: 'auto !important',
  },
  '@global .preview_container': {
    width: '100% !important',
    position: 'initial !important',
    height: 'auto !important',
    lineHeight: '1em !important',
    transform: 'none !important',
    padding: '0 !important',
  },
  '@global .preview_container.without_icon': {
    paddingRight: '0 !important',
  },
  '@global .time_picker_modal_header': {
    backgroundColor: `${theme.violet} !important`,
  },
  '@global .time_picker_modal_header .time_picker_header': {
    color: `#FFFFFF !important`,
  },
  '@global .time_picker_modal_header .time_picker_header_delivery': {
    color: `#FFFFFF !important`,
  },
  '@global .picker_center': {
    backgroundColor: `${theme.violet} !important`,
  },
  '@global .picker_pointer': {
    backgroundColor: `${theme.violet} !important`,
  },
  '@global .picker_pointer .pointer_drag': {
    backgroundColor: `${theme.violet} !important`,
  },
  '@global .picker_pointer .pointer_drag.draggable': {
    color: `#FFFFFF !important`,
  },
});

const getInitialValue = (placeholder: Date = new Date(), useCST: boolean) => {
  if (useCST && placeholder) {
    return formatInTimeZone(placeholder, 'America/Chicago', 'HH:mm');
  }
  return format(placeholder, 'HH:mm');
};

const FormTimeInput: FunctionComponent<Props> = ({
  classes,
  label,
  name,
  placeholder,
  size,
  disabled,
  useCST = true,
}) => {
  const initialValue = getInitialValue(placeholder, useCST);
  const [time, setTime] = useState<string>(initialValue);

  const { errors, setValue, trigger } = useFormContext();

  const handleTimeChange = ({ hour, minute }: Time) => {
    const newTime = `${hour}:${minute}`;
    setTime(newTime);
    setValue(name, `${newTime}:00`);
    trigger(name);
  };

  return (
    <div
      className={classnames(classes.container, {
        [classes.small]: size === 'small',
        [classes.medium]: size === 'medium',
        [classes.large]: size === 'large',
      })}
    >
      <label htmlFor={name}>
        <span className={classes.fieldLabel}>{label}</span>
        <div className={classes.timePickerWrapper}>
          <TimePicker
            withoutIcon
            timeMode={24}
            minuteStep={1}
            onTimeChange={handleTimeChange}
            time={time}
            autoMode={false}
            disabled={disabled}
          />
        </div>
      </label>
      {errors && <FormErrors name={name} errors={errors} />}
    </div>
  );
};

export default withStyles(styles)(FormTimeInput);
